import type { PlannerSport } from "./types";
import {
  SUP_PROFILE_KEYS,
  sportPresentation,
  type SupProfileKey,
} from "../conditions/sport-profiles";

export type PrefOption = { value: string; label: string };

type PrefFieldBase = {
  key: string;
  label: string;
  /** Conditional visibility based on the other selected values. */
  showIf?: (values: Record<string, string>) => boolean;
};

export type SelectPrefField = PrefFieldBase & {
  /** Existing select fields omit this marker; new fields declare it explicitly. */
  kind?: "select";
  options: PrefOption[];
  default: string;
};

export type FinitePositiveNumberPrefField = PrefFieldBase & {
  kind: "finite-positive-number";
  /** Kept for callers that enumerate defaults across all field kinds. */
  options: PrefOption[];
  default: "";
  unit: string;
  min: number;
  step: number;
};

export type PrefField = SelectPrefField | FinitePositiveNumberPrefField;

export type SportConfig = {
  sport: PlannerSport;
  /** Underlying scoring keys this planner sport can resolve to (the first is the default). */
  scoringKeys: string[];
  fields: PrefField[];
};

/** All scoring keys a planner sport may need (for fetching scores up front). */
export const SCORING_KEYS_FOR_SPORT: Record<PlannerSport, string[]> = {
  surf: ["surf"],
  bodyboarding: ["bodyboarding"],
  wind: ["wind"],
  foil: ["wing-foil", "foil", "downwind-foil"],
  sup: ["sup"],
  swim: ["swim"],
  diving: ["diving"],
  sailing: ["sailing-club", "sailing-hp"],
};

const ABILITY_4: PrefOption[] = [
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
  { value: "expert", label: "Expert" },
];

const ABILITY_3: PrefOption[] = [
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
];

const SUP_ABILITY: PrefOption[] = [
  { value: "unknown", label: "Not specified" },
  ...ABILITY_4,
];

const SUP_BOARD_TYPES: PrefOption[] = [
  { value: "unknown", label: "Not specified" },
  { value: "all-round", label: "All-round" },
  { value: "touring", label: "Touring" },
  { value: "surf", label: "Surf" },
  { value: "downwind", label: "Downwind" },
];

const SUP_COMPETENCE: PrefOption[] = [
  { value: "unknown", label: "Not specified" },
  { value: "learning", label: "Learning" },
  { value: "competent", label: "Competent" },
  { value: "advanced", label: "Advanced" },
];

const SUP_READINESS: PrefOption[] = [
  { value: "unknown", label: "Not specified" },
  { value: "planned", label: "Planned" },
  { value: "confirmed", label: "Confirmed" },
];

const NUMERIC_EMPTY_OPTION: PrefOption[] = [
  { value: "", label: "Not specified" },
];

const isSupProfile =
  (...profiles: readonly SupProfileKey[]) =>
  (values: Record<string, string>) =>
    profiles.includes(values.profile as SupProfileKey);

const SURF_TIMING: PrefOption[] = [
  { value: "dawn", label: "Dawn patrol" },
  { value: "morning", label: "Morning" },
  { value: "any", label: "Any time" },
  { value: "afternoon-evening", label: "Afternoon / evening" },
];

const PLANNER_CONFIGS_WITH_NAMED_SUP: Record<string, SportConfig> = {
  surf: {
    sport: "surf",
    scoringKeys: ["surf"],
    fields: [
      { key: "ability", label: "Ability", options: ABILITY_4, default: "intermediate" },
      {
        key: "board",
        label: "Board",
        options: [
          { value: "longboard", label: "Longboard" },
          { value: "funboard", label: "Funboard" },
          { value: "shortboard", label: "Shortboard" },
        ],
        default: "funboard",
      },
      { key: "timing", label: "Session timing", options: SURF_TIMING, default: "any" },
      {
        key: "crowd",
        label: "Crowds",
        options: [
          { value: "dont-mind", label: "Don't mind" },
          { value: "quieter", label: "Prefer quieter" },
        ],
        default: "dont-mind",
      },
    ],
  },

  bodyboarding: {
    sport: "bodyboarding",
    scoringKeys: ["bodyboarding"],
    fields: [
      { key: "ability", label: "Ability", options: ABILITY_4, default: "intermediate" },
      {
        key: "waveType",
        label: "Wave type",
        options: [
          { value: "unknown", label: "Use existing planner fit" },
          { value: "shorebreak", label: "Shorebreak" },
          { value: "hollow", label: "Hollow" },
          { value: "mellow", label: "Mellow reform" },
        ],
        default: "unknown",
      },
      { key: "timing", label: "Session timing", options: SURF_TIMING, default: "any" },
    ],
  },

  wind: {
    sport: "wind",
    scoringKeys: ["wind"],
    fields: [
      { key: "ability", label: "Ability", options: ABILITY_4, default: "intermediate" },
      {
        key: "discipline",
        label: "Discipline (context only)",
        options: [
          { value: "windsurf", label: "Windsurfing" },
          { value: "kitesurf", label: "Kitesurfing" },
        ],
        default: "kitesurf",
      },
      {
        key: "windRange",
        label: "Preferred wind",
        options: [
          { value: "light", label: "Light (12-16kt)" },
          { value: "moderate", label: "Moderate (16-22kt)" },
          { value: "strong", label: "Strong (22-30kt)" },
          { value: "storm", label: "Storm (30kt+)" },
        ],
        default: "moderate",
      },
      {
        key: "equipmentRange",
        label: "Equipment range (affects fit)",
        options: [
          { value: "unknown", label: "Not specified" },
          { value: "light-wind", label: "Light-wind kit" },
          { value: "all-round", label: "All-round kit" },
          { value: "high-wind", label: "High-wind kit" },
        ],
        default: "unknown",
      },
      {
        key: "timing",
        label: "Session timing",
        options: [
          { value: "any", label: "Any time" },
          { value: "afternoon-thermals", label: "Afternoon thermals" },
          { value: "morning-glassoff", label: "Morning glass-off" },
        ],
        default: "any",
      },
      {
        key: "waterState",
        label: "Water state",
        options: [
          { value: "flat", label: "Flat water" },
          { value: "chop", label: "Small chop" },
          { value: "waves", label: "Waves" },
        ],
        default: "chop",
      },
    ],
  },

  foil: {
    sport: "foil",
    // discipline selects the scorer at read time (see prefScoringKey)
    scoringKeys: ["wing-foil", "foil", "downwind-foil"],
    fields: [
      { key: "ability", label: "Ability", options: ABILITY_4, default: "intermediate" },
      {
        key: "discipline",
        label: "Discipline",
        options: [
          { value: "wing", label: "Wing foiling" },
          { value: "prone", label: "Prone / pump foiling" },
          { value: "downwind", label: "Downwind" },
        ],
        default: "wing",
      },
      {
        key: "boardSetup",
        label: "Board setup",
        options: [
          { value: "unknown", label: "Not specified" },
          { value: "stable", label: "Stable / higher-volume" },
          { value: "performance", label: "Performance / lower-volume" },
        ],
        default: "unknown",
      },
      {
        key: "foilSetup",
        label: "Foil setup",
        options: [
          { value: "unknown", label: "Not specified" },
          { value: "low-speed", label: "Low-speed lift" },
          { value: "all-round", label: "All-round" },
          { value: "high-speed", label: "High-speed control" },
        ],
        default: "unknown",
      },
      {
        key: "pumpingAbility",
        label: "Pumping ability",
        options: [
          { value: "unknown", label: "Not specified" },
          { value: "learning", label: "Learning to pump" },
          { value: "competent", label: "Competent pumping" },
          { value: "expert", label: "Expert pumping" },
        ],
        default: "unknown",
        showIf: (v) => v.discipline === "prone",
      },
      {
        key: "wingRange",
        label: "Wing range",
        options: [
          { value: "unknown", label: "Not specified" },
          { value: "light-wind", label: "Light-wind range" },
          { value: "all-round", label: "All-round range" },
          { value: "high-wind", label: "High-wind range" },
        ],
        default: "unknown",
        showIf: (v) => v.discipline === "wing",
      },
      {
        key: "windPref",
        label: "Preferred wind",
        options: [
          { value: "light", label: "Light (8-14kt)" },
          { value: "moderate", label: "Moderate (14-20kt)" },
          { value: "strong", label: "Strong (20kt+)" },
        ],
        default: "moderate",
        showIf: (v) => v.discipline === "wing",
      },
      {
        key: "swellPref",
        label: "Swell",
        options: [
          { value: "none", label: "Flat water" },
          { value: "small", label: "Small swell" },
          { value: "moderate", label: "Moderate swell" },
        ],
        default: "small",
        showIf: (v) => v.discipline === "prone" || v.discipline === "downwind",
      },
    ],
  },

  sup: {
    sport: "sup",
    scoringKeys: [...SUP_PROFILE_KEYS],
    fields: [
      {
        kind: "select",
        key: "profile",
        label: "SUP profile",
        options: SUP_PROFILE_KEYS.map((profile) => ({
          value: profile,
          label: `${sportPresentation(profile).label} Beta`,
        })),
        default: "sup-touring",
      },
      {
        kind: "select",
        key: "ability",
        label: "Ability",
        options: SUP_ABILITY,
        default: "unknown",
        showIf: isSupProfile("sup-touring"),
      },
      {
        kind: "select",
        key: "boardType",
        label: "Board type",
        options: SUP_BOARD_TYPES,
        default: "unknown",
      },
      {
        kind: "finite-positive-number",
        key: "boardVolumeL",
        label: "Board volume",
        options: NUMERIC_EMPTY_OPTION,
        default: "",
        unit: "litres",
        min: 0.1,
        step: 0.1,
        showIf: isSupProfile("sup-surf"),
      },
      {
        kind: "finite-positive-number",
        key: "riderMassKg",
        label: "Rider mass",
        options: NUMERIC_EMPTY_OPTION,
        default: "",
        unit: "kg",
        min: 0.1,
        step: 0.1,
        showIf: isSupProfile("sup-surf"),
      },
      {
        kind: "finite-positive-number",
        key: "boardLengthFt",
        label: "Board length",
        options: NUMERIC_EMPTY_OPTION,
        default: "",
        unit: "feet",
        min: 0.1,
        step: 0.1,
        showIf: isSupProfile("sup-downwind"),
      },
      {
        kind: "select",
        key: "surfCompetence",
        label: "Surf competence",
        options: SUP_COMPETENCE,
        default: "unknown",
        showIf: isSupProfile("sup-surf"),
      },
      {
        kind: "finite-positive-number",
        key: "waveComfortMaxM",
        label: "Wave comfort",
        options: NUMERIC_EMPTY_OPTION,
        default: "",
        unit: "metres",
        min: 0.1,
        step: 0.1,
        showIf: isSupProfile("sup-surf"),
      },
      {
        kind: "select",
        key: "leash",
        label: "Leash",
        options: SUP_READINESS,
        default: "unknown",
        showIf: isSupProfile("sup-surf"),
      },
      {
        kind: "select",
        key: "safetyKit",
        label: "Safety kit",
        options: SUP_READINESS,
        default: "unknown",
        showIf: isSupProfile("sup-surf"),
      },
      {
        kind: "select",
        key: "launchCompetence",
        label: "Launch competence",
        options: SUP_COMPETENCE,
        default: "unknown",
        showIf: isSupProfile("sup-surf"),
      },
      {
        kind: "select",
        key: "downwindCompetence",
        label: "Downwind competence",
        options: SUP_COMPETENCE,
        default: "unknown",
        showIf: isSupProfile("sup-downwind"),
      },
      {
        kind: "finite-positive-number",
        key: "plannedDistanceKm",
        label: "Planned distance",
        options: NUMERIC_EMPTY_OPTION,
        default: "",
        unit: "kilometres",
        min: 0.1,
        step: 0.1,
        showIf: isSupProfile("sup-touring", "sup-downwind"),
      },
      {
        kind: "finite-positive-number",
        key: "comfortableDistanceKm",
        label: "Comfortable distance",
        options: NUMERIC_EMPTY_OPTION,
        default: "",
        unit: "kilometres",
        min: 0.1,
        step: 0.1,
        showIf: isSupProfile("sup-touring", "sup-downwind"),
      },
      {
        kind: "select",
        key: "communications",
        label: "Communications",
        options: SUP_READINESS,
        default: "unknown",
        showIf: isSupProfile("sup-downwind"),
      },
      {
        kind: "select",
        key: "selfRescue",
        label: "Self-rescue",
        options: SUP_COMPETENCE,
        default: "unknown",
        showIf: isSupProfile("sup-downwind"),
      },
      {
        kind: "select",
        key: "routePlan",
        label: "Route plan",
        options: SUP_READINESS,
        default: "unknown",
        showIf: isSupProfile("sup-downwind"),
      },
      {
        kind: "select",
        key: "recoveryPlan",
        label: "Recovery plan",
        options: SUP_READINESS,
        default: "unknown",
        showIf: isSupProfile("sup-downwind"),
      },
    ],
  },

  swim: {
    sport: "swim",
    scoringKeys: ["swim"],
    fields: [
      { key: "ability", label: "Ability", options: ABILITY_4, default: "intermediate" },
      {
        key: "tempComfort",
        label: "Water temperature",
        options: [
          { value: "warm", label: "Warm only (18C+)" },
          { value: "moderate", label: "Moderate (14-18C)" },
          { value: "cold", label: "Cold-water swimmer" },
        ],
        default: "moderate",
      },
      {
        key: "windTol",
        label: "Wind tolerance",
        options: [
          { value: "calm", label: "Calm only" },
          { value: "light", label: "Light wind ok" },
        ],
        default: "light",
      },
      {
        key: "waterQuality",
        label: "Water quality",
        options: [
          { value: "high", label: "Only when good" },
          { value: "moderate", label: "Moderate" },
          { value: "relaxed", label: "Not concerned" },
        ],
        default: "moderate",
      },
      {
        key: "sessionLength",
        label: "Session length",
        options: [
          { value: "short", label: "Short (30 min)" },
          { value: "standard", label: "Standard (1 hour)" },
          { value: "long", label: "Long (1.5h+)" },
        ],
        default: "standard",
      },
    ],
  },

  diving: {
    sport: "diving",
    scoringKeys: ["diving"],
    fields: [
      { key: "ability", label: "Diving experience", options: ABILITY_4, default: "intermediate" },
      {
        key: "mode",
        label: "Dive mode",
        options: [
          { value: "recreational", label: "Recreational" },
          { value: "drift", label: "Planned drift" },
          { value: "low-visibility", label: "Low visibility" },
        ],
        default: "recreational",
      },
      {
        key: "training",
        label: "Training context",
        options: [
          { value: "not-specified", label: "Not specified" },
          { value: "advanced", label: "Advanced training" },
          { value: "current-trained", label: "Current training" },
          { value: "low-visibility-trained", label: "Low-visibility training" },
        ],
        default: "not-specified",
      },
      {
        key: "plannedDepth",
        label: "Planned depth",
        options: [
          { value: "shallow", label: "Shallow, under 10m" },
          { value: "moderate", label: "Moderate, 10-20m" },
          { value: "deep", label: "Deep, over 20m" },
        ],
        default: "moderate",
      },
      {
        key: "entry",
        label: "Entry",
        options: [
          { value: "shore", label: "Shore entry" },
          { value: "boat", label: "Boat entry" },
        ],
        default: "shore",
      },
    ],
  },

  sailing: {
    sport: "sailing",
    scoringKeys: ["sailing-hp", "sailing-club"],
    fields: [
      { key: "ability", label: "Ability", options: ABILITY_4, default: "intermediate" },
      {
        key: "boat",
        label: "Boat type",
        options: [
          { value: "dinghy", label: "Dinghy" },
          { value: "keelboat", label: "Keelboat" },
          { value: "catamaran", label: "Catamaran" },
        ],
        default: "dinghy",
      },
      {
        key: "windRange",
        label: "Preferred wind",
        options: [
          { value: "light", label: "Light (6-12kt)" },
          { value: "moderate", label: "Moderate (12-18kt)" },
          { value: "fresh", label: "Fresh (18-25kt)" },
          { value: "strong", label: "Strong (25kt+)" },
        ],
        default: "moderate",
      },
      {
        key: "seaState",
        label: "Sea state",
        options: [
          { value: "calm", label: "Calm" },
          { value: "moderate", label: "Moderate chop" },
          { value: "rough", label: "Rough water ok" },
        ],
        default: "moderate",
      },
    ],
  },
};

const LEGACY_SUP_CONFIG: SportConfig = {
  sport: "sup",
  scoringKeys: ["sup"],
  fields: [
    { key: "ability", label: "Ability", options: ABILITY_3, default: "intermediate" },
    {
      key: "profile",
      label: "SUP profile",
      options: [{ value: "sup", label: "Legacy Touring SUP" }],
      default: "sup",
    },
    {
      key: "style",
      label: "Legacy style (inactive)",
      options: [
        { value: "touring", label: "Flatwater touring" },
        { value: "surf", label: "Surf SUP" },
        { value: "fitness", label: "SUP fitness" },
        { value: "downwind", label: "Downwind" },
      ],
      default: "touring",
      showIf: () => false,
    },
    {
      key: "windTol",
      label: "Legacy wind tolerance (inactive)",
      options: [
        { value: "calm", label: "Calm only" },
        { value: "light", label: "Light wind ok" },
        { value: "moderate", label: "Moderate wind ok" },
      ],
      default: "light",
      showIf: () => false,
    },
    {
      key: "waterState",
      label: "Legacy water state (inactive)",
      options: [
        { value: "flat", label: "Flat" },
        { value: "chop", label: "Small chop ok" },
        { value: "waves", label: "Waves ok" },
      ],
      default: "flat",
      showIf: () => false,
    },
  ],
};

/**
 * Static configuration stays on legacy SUP. Named controls are returned only
 * for the exact profiles granted by the server-side public release boundary.
 */
export const SPORT_CONFIGS: Record<string, SportConfig> = {
  ...PLANNER_CONFIGS_WITH_NAMED_SUP,
  sup: LEGACY_SUP_CONFIG,
};

export function sportConfigForPlanner(
  sport: string,
  availableSupProfiles: readonly SupProfileKey[] = [],
): SportConfig {
  const config = SPORT_CONFIGS[sport];
  if (!config) throw new Error(`Unknown planner sport: ${sport}`);
  if (sport !== "sup") return config;

  const grantedProfiles = SUP_PROFILE_KEYS.filter((profile) =>
    availableSupProfiles.includes(profile),
  );
  if (grantedProfiles.length === 0) return config;

  const namedConfig = PLANNER_CONFIGS_WITH_NAMED_SUP.sup;
  return {
    ...namedConfig,
    scoringKeys: [...grantedProfiles],
    fields: namedConfig.fields.map((field): PrefField => {
      if (field.key !== "profile" || field.kind === "finite-positive-number") {
        return field;
      }
      return {
        ...field,
        options: field.options.filter((option) =>
          grantedProfiles.includes(option.value as SupProfileKey),
        ),
        default: grantedProfiles[0],
      };
    }),
  };
}
